import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Toggler from '../components/Toggler';
import ProductComparisonRows from '../components/ProductComparisonRows';


class ComparisonTable extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isExpanded: true,
    };

    this.toggleAttributes = this.toggleAttributes.bind(this);
  }

  static getDomId() {
    return 'product_comparison_table';
  }

  static getComponentData() {
    const componentData = window.seMarketplace.product;

    if (!componentData || !componentData.comparisonAttributes) {
      return null;
    }

    const customComparisonAttrKeys = componentData.comparisonAttributes;
    const comparisonAttrKeys = this.getComparisonAttrKeys(customComparisonAttrKeys, componentData);
    const productAttributes = this.getProductAttributes(
      componentData,
      comparisonAttrKeys,
      customComparisonAttrKeys,
    );
    const comparisonProducts = [productAttributes];

    componentData.comparisonProducts.forEach((comparisonProduct) => {
      comparisonProducts.push(
        this.getProductAttributes(
          comparisonProduct,
          comparisonAttrKeys,
          customComparisonAttrKeys,
        ),
      );
    });

    const productComparisonRows = this.buildProductComparisonRows(
      comparisonAttrKeys, comparisonProducts,
    );

    return {
      productComparisonRows: productComparisonRows.filter(row => row),
    };
  }

  /**
   * Get an array of product attributes that we can create comparisons on.
   * This list includes a set of base attributes, as well as configurable
   * attributes that are stored on the product.
   *
   * @param { Array } customComparisonAttrKeys - Configurable attributes stored on a product
   * @param { Object } product - A product which has attribute values
   * @return { Array } - A list of all potential attributes to get values for
  */
  static getComparisonAttrKeys(customComparisonAttrKeys, product) {
    const baseAttrKeys = [
      'image',
      'name',
      'price',
      'rebate',
      'rating',
    ];

    const availableComparisonAttrKeys = [];

    customComparisonAttrKeys.forEach((attrKey) => {
      let comparisonAttrFound = false;

      Object.keys(product.attributeGroups).forEach((attrGroupKey) => {
        if (attrKey in product.attributeGroups[attrGroupKey]) {
          comparisonAttrFound = true;
        }
      });

      product.comparisonProducts.forEach((comparisonProduct) => {
        Object.keys(comparisonProduct.attributeGroups).forEach((attrGroupKey) => {
          if (attrKey in comparisonProduct.attributeGroups[attrGroupKey]) {
            comparisonAttrFound = true;
          }
        });
      });

      if (comparisonAttrFound) {
        availableComparisonAttrKeys.push(attrKey);
      }
    });

    return baseAttrKeys.concat(availableComparisonAttrKeys);
  }

  /**
   * Get formatted product attributes for use in building comparison table
   *
   * @param { Object } product - A product to get attributes from
   * @param { Array } comparisonAttrKeys - All attribute keys to try to retrieve from the product
   * @param { Array } customComparisonAttrKeys - Attribute keys that can be folded in the UI
   * @return { Object } - Formatted product attributes
  */
  static getProductAttributes(product, comparisonAttrKeys, customComparisonAttrKeys) {
    if (!product.attributeGroups) {
      return false;
    }

    const price = product.price.sales.formatted;

    const baseAttrs = {
      image: {
        label: 'Image',
        value: product.images.productTile ? product.images.productTile[0] : {},
        type: 'image',
      },
      name: {
        label: 'Product',
        value: {
          text: product.productName,
          url: product.productUrl,
        },
        type: 'productName',
      },
      price: {
        label: 'Price',
        value: price,
      },
      rebate: {
        label: 'Rebate',
        value: product.rebates.max.formatted,
      },
      rating: {
        label: 'Average rating',
        value: {
          ...product.ratingSummary,
          url: product.productUrl,
        },
        type: 'rating',
      },
    };

    const customAttrs = {};

    customComparisonAttrKeys.forEach((key) => {
      if (product[key]) {
        customAttrs[key] = {
          label: key,
          value: product[key],
          isCustom: true,
        };
      } else {
        Object.keys(product.attributeGroups).forEach((attributeGroup) => {
          const attr = product.attributeGroups[attributeGroup][key];

          if (attr) {
            customAttrs[key] = {
              label: attr.label,
              value: attr.value.join(', '),
              isCustom: true,
            };
          }
        });
      }
    });

    const attributes = Object.assign(baseAttrs, customAttrs);

    const orderedAttributes = {};

    comparisonAttrKeys.forEach((key) => {
      if (Object.hasOwnProperty.call(attributes, key)) {
        orderedAttributes[key] = attributes[key];
      } else {
        orderedAttributes[key] = {};
      }
    });

    return orderedAttributes;
  }

  /**
   * Create a table-friendly representation of product attributes for comparison
   *
   * @param { Array } comparisonAttrKeys - Product attributes which will build the table's rows
   * @param { Array } comparisonProducts - A list of products which will build the table's columns
   * @return { Array } - A table-friendly representation of product comparisons
  */
  static buildProductComparisonRows(comparisonAttrKeys, comparisonProducts) {
    const productComparisonRows = [];

    comparisonAttrKeys.forEach(key => productComparisonRows.push({
      key,
      rowHeaderLabel: '',
      rowCells: [],
      displayRowHeaderLabel: key !== 'image',
      isCustom: false,
    }));

    productComparisonRows.forEach((row, i) => {
      comparisonProducts.forEach((product) => {
        const { key } = row;

        const productAttr = product[key];

        if (productAttr) {
          if (productComparisonRows[i].rowHeaderLabel === '' && productAttr.label) {
            productComparisonRows[i].rowHeaderLabel = productAttr.label;
          }

          productComparisonRows[i].isCustom = !!productAttr.isCustom;

          return productComparisonRows[i].rowCells.push(productAttr);
        }
      });
    });

    return productComparisonRows;
  }

  toggleAttributes() {
    const { isExpanded } = this.state;

    this.setState({ isExpanded: !isExpanded });
  }

  render() {
    const { productComparisonRows } = this.props;
    const { isExpanded } = this.state;

    if (!productComparisonRows) {
      return null;
    }

    return (
      <div className="se-tier">
        <div className="se-container">
          <h2 id="product-table__title" className="product-table__title">Compare with similar items</h2>
          <div id="table-container">
          <table className="product-table__holder" aria-labelledby="product-table__title" aria-rowcount={productComparisonRows.length}>
            <tbody>
              <ProductComparisonRows rows={productComparisonRows} isExpanded={isExpanded} />
            </tbody>
          </table>
          </div>
          <Toggler isExpanded={isExpanded} action={this.toggleAttributes} />
        </div>
      </div>
    );
  }
}

ComparisonTable.propTypes = {
  productComparisonRows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

export default ComparisonTable;
